/**
 * EtFormulekaart — de formulekaart als hulpmiddel (Facet-blauwdruk §10 punt 4).
 *
 * Op het digitale bb/kb-examen kan de leerling een formulekaart openen met de
 * standaard meetkundeformules. Dit is onze eigen weergave van die standaard-
 * wiskundeformules (feitelijke wiskunde, eigen opmaak en indeling), in
 * TestWisely-stijl. Zwevend, sleepbaar paneel — zelfde gedrag als de
 * rekenmachine.
 *
 * Ook in een gewone toets/communicatiesessie (01-11-2026, ToetsHulpmiddelen):
 * gedockt in de zijkolom naast de vraag; Esc sluit (focus in het paneel).
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import './EtFormulekaart.css';

const SECTIES = [
  {
    kop: 'Vlakke figuren',
    regels: [
      ['Omtrek rechthoek', '2 × (lengte + breedte)'],
      ['Oppervlakte rechthoek', 'lengte × breedte'],
      ['Oppervlakte driehoek', '(basis × hoogte) ÷ 2'],
      ['Omtrek cirkel', 'π × diameter'],
      ['Oppervlakte cirkel', 'π × straal × straal'],
    ],
  },
  {
    kop: 'Ruimtefiguren',
    regels: [
      ['Inhoud balk', 'lengte × breedte × hoogte'],
      ['Inhoud cilinder', 'oppervlakte grondvlak × hoogte'],
      ['Inhoud prisma', 'oppervlakte grondvlak × hoogte'],
      ['Inhoud piramide', '(oppervlakte grondvlak × hoogte) ÷ 3'],
      ['Inhoud kegel', '(oppervlakte grondvlak × hoogte) ÷ 3'],
      ['Inhoud bol', '4⁄3 × π × straal³'],
    ],
  },
  {
    kop: 'Rekenen',
    regels: [
      ['Gemiddelde', 'som van de getallen ÷ aantal getallen'],
      ['Snelheid', 'afstand ÷ tijd'],
      ['Procent nemen', 'deel ÷ geheel × 100'],
    ],
  },
];

export default function EtFormulekaart({ open, onSluit, gedockt = false, autoFocus = false }) {
  const [pos, setPos] = useState({ x: null, y: null });
  const sleepRef = useRef(null);

  const startSleep = useCallback((e) => {
    const paneel = sleepRef.current;
    if (!paneel) return;
    const rect = paneel.getBoundingClientRect();
    const dx = e.clientX - rect.left, dy = e.clientY - rect.top;
    const move = (ev) => setPos({ x: ev.clientX - dx, y: ev.clientY - dy });
    const stop = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', stop); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  }, []);

  useEffect(() => { if (!open) setPos({ x: null, y: null }); }, [open]);
  useEffect(() => {
    if (open && autoFocus && sleepRef.current) sleepRef.current.focus();
  }, [open, autoFocus]);

  if (!open) return null;
  // Gesleept = zwevend (ook als hij gedockt begon).
  const stijl = pos.x === null ? {} : { position: 'fixed', left: pos.x, top: pos.y, right: 'auto' };
  const opToets = (e) => {
    if (e.key === 'Escape' && onSluit) { e.preventDefault(); e.stopPropagation(); onSluit(); }
  };

  return (
    <div ref={sleepRef} className={`etf-paneel${gedockt ? ' etf-paneel--gedockt' : ''}`} style={stijl}
      role="dialog" aria-label="Formulekaart" tabIndex={-1} onKeyDown={opToets}
      data-testid="formulekaart">
      <div className="etf-kop" onPointerDown={(e) => { if (e.target.closest('button')) return; startSleep(e); }}>
        <span>Formulekaart</span>
        <button className="etf-sluit" onClick={onSluit} aria-label="Sluit formulekaart">×</button>
      </div>
      <div className="etf-inhoud">
        {SECTIES.map((s) => (
          <section key={s.kop}>
            <h4>{s.kop}</h4>
            <dl>
              {s.regels.map(([naam, formule]) => (
                <div key={naam}><dt>{naam}</dt><dd>{formule}</dd></div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </div>
  );
}
